/**
 * React Query hooks for SMTP settings — mirrors `lib/distributors.ts`:
 * - per-org settings (`/api/orgs/{slug}/smtp-settings`)
 * - system-wide settings (`/api/admin/smtp-settings`)
 * - test endpoint (POST /test on either) sends a real canary message
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type SmtpEncryption = 'none' | 'starttls' | 'ssl';

export interface SmtpTestResult {
  ok: boolean;
  detail: string;
  source?: 'system' | 'org' | 'env';
}

// --- System-wide ---------------------------------------------------------

export interface SystemSmtpSettings {
  enabled: boolean;
  host: string | null;
  port: number;
  encryption: SmtpEncryption;
  username: string | null;
  has_password: boolean;
  from_address: string | null;
  from_name: string | null;
  last_error: string | null;
  last_error_at: string | null;
  blocked_org_slugs: string[];
}

export interface SystemSmtpSettingsUpdate {
  enabled: boolean;
  host?: string | null;
  port?: number;
  encryption: SmtpEncryption;
  username?: string | null;
  password?: string | null;
  clear_password?: boolean;
  from_address?: string | null;
  from_name?: string | null;
}

export function useSystemSmtpSettings() {
  return useQuery<SystemSmtpSettings>({
    queryKey: ['system-smtp-settings'],
    queryFn: () => api.get<SystemSmtpSettings>('/api/admin/smtp-settings'),
    staleTime: 30_000,
  });
}

export function useUpdateSystemSmtpSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SystemSmtpSettingsUpdate) =>
      api.putJson<SystemSmtpSettings>('/api/admin/smtp-settings', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-smtp-settings'] });
      qc.invalidateQueries({ queryKey: ['org-smtp-settings'] });
    },
  });
}

export function useTestSystemSmtp() {
  return useMutation({
    mutationFn: (to: string) =>
      api.postJson<SmtpTestResult>('/api/admin/smtp-settings/test', { to }),
  });
}

// --- Per-org -------------------------------------------------------------

export interface OrgSmtpSettings {
  enabled: boolean;
  use_system_credentials: boolean;
  system_credentials_allowed: boolean;
  system_credentials_enabled: boolean;
  host: string | null;
  port: number;
  encryption: SmtpEncryption;
  username: string | null;
  has_org_password: boolean;
  from_address: string | null;
  from_name: string | null;
  last_error: string | null;
  last_error_at: string | null;
}

export interface OrgSmtpSettingsUpdate {
  enabled: boolean;
  use_system_credentials: boolean;
  host?: string | null;
  port?: number;
  encryption: SmtpEncryption;
  username?: string | null;
  password?: string | null;
  clear_password?: boolean;
  from_address?: string | null;
  from_name?: string | null;
}

export function useOrgSmtpSettings(orgSlug: string | undefined) {
  return useQuery<OrgSmtpSettings>({
    queryKey: ['org-smtp-settings', orgSlug],
    queryFn: () =>
      api.get<OrgSmtpSettings>(`/api/orgs/${orgSlug}/smtp-settings`),
    enabled: Boolean(orgSlug),
    staleTime: 30_000,
  });
}

export function useUpdateOrgSmtpSettings(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrgSmtpSettingsUpdate) =>
      api.putJson<OrgSmtpSettings>(
        `/api/orgs/${orgSlug}/smtp-settings`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['org-smtp-settings', orgSlug] }),
  });
}

export function useTestOrgSmtp(orgSlug: string | undefined) {
  return useMutation({
    mutationFn: (to: string) =>
      api.postJson<SmtpTestResult>(
        `/api/orgs/${orgSlug}/smtp-settings/test`,
        { to },
      ),
  });
}

/**
 * Imperative reveal-on-demand fetchers for the SMTP password fields.
 * Not React-Query hooks — the UI calls these from a button click and
 * displays the value transiently, so caching would defeat the
 * "reveal only on explicit gesture" intent.
 */
export async function revealSystemSmtpPassword(): Promise<string | null> {
  const res = await api.postJson<{ password: string | null }>(
    '/api/admin/smtp-settings/reveal-password',
    {},
  );
  return res.password;
}

export async function revealOrgSmtpPassword(
  orgSlug: string,
): Promise<string | null> {
  const res = await api.postJson<{ password: string | null }>(
    `/api/orgs/${orgSlug}/smtp-settings/reveal-password`,
    {},
  );
  return res.password;
}

export function useSetOrgSmtpAllowlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { target_org_slug: string; allowed: boolean }) =>
      api.putJson<OrgSmtpSettings>(
        `/api/admin/smtp-settings/orgs/${input.target_org_slug}/allowlist`,
        { allowed: input.allowed },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-smtp-settings'] });
      qc.invalidateQueries({ queryKey: ['org-smtp-settings'] });
    },
  });
}
